// The frame of the public pages: the lockup, a language toggle, one card in the middle, and "Manage cookies".
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../session';
import { ManageCookies } from './CookieBanner';
import { BackToSite, LanguageToggle } from './ui';

const WIDTHS = { narrow: 'max-w-[400px]', wide: 'max-w-[520px]', xl: 'max-w-[1040px]' };

export function PublicLayout({ title, subtitle, children, wide = false, size, back }: { title: string; subtitle?: string; children: ReactNode; wide?: boolean; size?: keyof typeof WIDTHS; back?: ReactNode }) {
  const { t } = useTranslation();
  const { setLocale } = useSession();
  return (
    <div className="flex min-h-screen flex-col bg-pane">
      <div className="flex items-center justify-between px-4 py-3">
        <div>{back ?? <BackToSite />}</div>
        <LanguageToggle onChange={(l) => void setLocale(l)} />
      </div>
      <main className={`mx-auto flex w-full flex-1 flex-col justify-center gap-5 px-4 pb-12 ${WIDTHS[size ?? (wide ? 'wide' : 'narrow')]}`}>
        {/* The Sculpt Agent lockup: the mark and the name. */}
        <span className="mb-1 inline-flex items-center gap-2.5 self-center" role="img" aria-label="Sculpt Agent">
          <SculptMark size={44} />
          <span aria-hidden="true" className="text-[26px] font-semibold leading-none tracking-[-0.03em]">
            <span className="text-ink">Sculpt</span> <span className="text-accent">Agent</span>
          </span>
        </span>
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[22px] font-medium leading-[1.2] tracking-[-0.02em] text-ink">{title}</h1>
          {subtitle && <p className="text-[13.5px] leading-[1.45] text-muted">{subtitle}</p>}
        </div>
        {children}
        <p className="mt-6 text-center font-mono text-[10.5px] uppercase tracking-[0.06em] text-fainter">Sculpt · {t('app.byline')}</p>
        <ManageCookies className="-mt-3" />
      </main>
    </div>
  );
}

/** The Sculpt Agent mark (public/brand/sculpt-agent-icon.svg cropped to the tile), inline so it sits flush with the name. */
function SculptMark({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="100 150 780 780" aria-hidden="true">
      <g transform="translate(119.47,221.87)">
        <rect x="0" y="0" width="682.67" height="682.67" rx="136.53" fill="#107C41"/>
        <rect x="119.47" y="119.47" width="443.73" height="443.73" rx="51.2" fill="none" stroke="#FFFFFF" strokeWidth="25.6"/>
        <g stroke="#FFFFFF" strokeWidth="25.6">
          <line x1="119.47" y1="264.53" x2="563.2" y2="264.53"/><line x1="119.47" y1="418.13" x2="563.2" y2="418.13"/>
          <line x1="264.53" y1="119.47" x2="264.53" y2="563.2"/><line x1="418.13" y1="119.47" x2="418.13" y2="563.2"/>
        </g>
        <rect x="264.53" y="264.53" width="153.6" height="153.6" fill="#FFFFFF"/>
        <circle cx="631.47" cy="51.2" r="128.0" fill="#FFFFFF"/>
        <path d="M631.47 -51.2 L657.07 25.6 L733.87 51.2 L657.07 76.8 L631.47 153.6 L605.87 76.8 L529.07 51.2 L605.87 25.6 Z" fill="#107C41"/>
      </g>
    </svg>
  );
}
